import type { MouseEventHandler, RefObject } from "react";
import * as React from "react";

import clsx from "clsx";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import type { TextProps } from "@busbud/horizon";
import { Link } from "@busbud/horizon";

import type { Pluggable } from "unified";

import { MappedHeading, MappedList } from "./components";
import { Paragraph } from "../common";

interface Props {
  content?: string;
  children?: string;
  className?: string;
  innerRef?: RefObject<any>;
  body_font_size?: TextProps["size"] | "responsive";
  link_target_blank?: boolean;
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
}

type MarkdownLinkProps = {
  href?: string;
  target_blank: boolean;
  children: React.ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

const MarkdownLink = (props: MarkdownLinkProps) => {
  const { target_blank, href, children, ...rest } = props;

  return (
    <Link
      url={href}
      target={target_blank ? "_blank" : undefined}
      disableVisitedStyles
      {...rest}
    >
      {children}
    </Link>
  );
};

export const Markdown: React.FC<Props> = props => {
  const {
    content,
    children,
    className,
    body_font_size,
    link_target_blank = false,
    onLinkClick
  } = props;

  const components = React.useMemo(() => {
    const size: TextProps["size"] =
      body_font_size === "responsive"
        ? { initial: "md", sm: "lg" }
        : body_font_size;

    return {
      h1: ({ children }) => <MappedHeading level={1}>{children}</MappedHeading>,
      h2: ({ children }) => <MappedHeading level={2}>{children}</MappedHeading>,
      h3: ({ children }) => <MappedHeading level={3}>{children}</MappedHeading>,
      h4: ({ children }) => <MappedHeading level={4}>{children}</MappedHeading>,
      h5: ({ children }) => <MappedHeading level={5}>{children}</MappedHeading>,
      h6: ({ children }) => <MappedHeading level={6}>{children}</MappedHeading>,
      ul: ({ children }) => <MappedList ordered={false}>{children}</MappedList>,
      ol: ({ children }) => <MappedList ordered={true}>{children}</MappedList>,
      p: ({ children, color, ...other_props }) => {
        return (
          <Paragraph size={size} {...other_props}>
            {children}
          </Paragraph>
        );
      },
      a: ({ children, ...props }) => (
        <MarkdownLink
          {...props}
          onClick={onLinkClick}
          target_blank={link_target_blank}
        >
          {children}
        </MarkdownLink>
      )
    } satisfies Components;
  }, [body_font_size, onLinkClick, link_target_blank]);

  const content_to_render: string | undefined = content || children;
  if (!content_to_render) {
    return null;
  }

  return (
    <div
      data-testid="markdown"
      className={clsx(
        "last:*:mb-0 [&>a]:text-color-scheme-interactive-link-600 [&>a]:visited:text-color-scheme-literal-purple-600 [&>a]:hover:text-color-scheme-interactive-link-500 [&>a]:focus-visible:outline-none [&>a]:focus-visible:ring [&>a]:active:text-color-scheme-interactive-link-600 [&>a]:aria-disabled:cursor-default",
        className
      )}
    >
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkBreaks as Pluggable, remarkGfm as Pluggable]}
      >
        {content_to_render}
      </ReactMarkdown>
    </div>
  );
};
